/**
 * T2436 — brez sedeža ni dostopa do modula.
 *
 * Zaledje (`GET me/modules/`) modul, za katerega uporabnik nima sedeža, iz
 * `modules` izpusti in ga navede v `seatless_modules`; projekti modula pa
 * vračajo 404 (`resolve_project_access`). Brez teh vrat bi neposredna povezava
 * (zaznamek, obvestilo) pripeljala na prazen seznam ali na »projekt ne
 * obstaja« — razlaga, ki je napačna in ne pove, kaj storiti.
 *
 * Vrata **ne zaklenejo ob neznanem stanju** (isto pravilo kot `hasSeat`,
 * T1228): dokler `seatless_modules` modula izrecno ne navaja, gre uporabnik
 * naprej. Lastnik/administrator brez sedeža modul obdrži v `modules` in gre
 * skozi — v načinu samo za branje, ki ga razložijo obstoječi namigi
 * (`SeatGateNotice`).
 *
 * Uporaba kot ovojnica (`children`) ali kot postavitvena ruta (`<Outlet/>`).
 */
import type { ReactNode } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

import { useModuleStore } from '../../store/moduleStore';

interface RequireModuleSeatProps {
  /** Slug modula, kot ga vrne `me/modules/` (npr. `quantify`). */
  slug: string;
  children?: ReactNode;
}

export function RequireModuleSeat({ slug, children }: RequireModuleSeatProps) {
  const { t } = useTranslation();
  const seatless = useModuleStore((s) => s.isSeatless(slug));

  if (!seatless) {
    return <>{children ?? <Outlet />}</>;
  }

  return (
    <div
      role="status"
      data-testid="module-seat-required"
      className="flex flex-col items-center justify-center gap-3 text-center py-24 px-6"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300">
        <Lock className="h-6 w-6" aria-hidden="true" />
      </div>
      <h2 className="text-title font-bold text-foreground">{t('seats.no_access_title')}</h2>
      <p className="text-body leading-relaxed text-muted-foreground max-w-md">
        {t('seats.no_access_desc')}
      </p>
      <Link
        to="/dashboard"
        data-testid="module-seat-required-dashboard"
        className="inline-flex items-center justify-center font-bold transition-colors px-5 py-2.5 rounded-xl text-body border border-border text-foreground hover:bg-accent"
      >
        {t('seats.no_access_cta')}
      </Link>
    </div>
  );
}
